import React, { useState } from "react";
import { useGetPobImagesQuery } from "state/api";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Modal,
  Grid,
  Button,
  Chip,
  useMediaQuery,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import RingLoader from "react-spinners/RingLoader";
import { Autocomplete, TextField } from "@mui/material";
import FlexBetween from "components/FlexBetween";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";

const PobImagePreviewModal = () => {
  const { data: appdata, error, isLoading } = useGetPobImagesQuery();
  const [previewImage, setPreviewImage] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedEmpNo, setSelectedEmpNo] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const handlePreviewImage = (pic) => {
    setPreviewImage(pic);
    setIsFullscreen(false);
  };

  const handleDownload = (imageUrl) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = "prescription.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add these variable declarations before return statement
  const sortedData = [...(appdata || [])].sort(
    (a, b) => new Date(b.SUBDATE) - new Date(a.SUBDATE),
  );

  const uniqueEmpNos = [...new Set(sortedData.map((item) => item.EmpNo))];

  const filteredData =
    selectedEmpNo && selectedEmpNo !== "All"
      ? sortedData.filter((item) => item.EmpNo === selectedEmpNo)
      : sortedData;

  // ... existing handlers and logic ...

  const CustomSpinner = () => (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{ backdropFilter: "blur(5px)" }}
    >
      <RingLoader color={theme.palette.primary.main} size={100} />
    </Box>
  );

  if (isLoading) return <CustomSpinner />;

  if (error)
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 3,
          textAlign: "center",
          padding: 4,
          background: theme.palette.background.default,
        }}
      >
        <Typography variant="h5" color="error">
          Failed to Load Data
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {error.message}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Try Again
        </Button>
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.background.default,
        padding: isMobile ? 2 : 4,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          boxShadow: 3,
          borderRadius: 2,
          mb: 4,
          p: 3,
        }}
      >
        <FlexBetween>
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIosNew />}
            sx={{
              color: theme.palette.common.white,
              "&:hover": {
                background: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontWeight: 700,
              textShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            Prescriptions
          </Typography>
          <Box sx={{ width: 100 }} /> {/* Spacer */}
        </FlexBetween>

        {/* Filter Section */}
        <Box
          sx={{
            mt: 3,
            display: "flex",
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Autocomplete
            options={["All", ...uniqueEmpNos]}
            value={selectedEmpNo}
            onChange={(event, newValue) => setSelectedEmpNo(newValue || "All")}
            sx={{ flexGrow: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Employee"
                variant="filled"
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  "& .MuiFilledInput-root": {
                    background: "transparent",
                  },
                }}
              />
            )}
          />

          <Button
            onClick={() => setSelectedEmpNo("All")}
            variant="contained"
            color="secondary"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 4,
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>

      {/* Content Grid */}
      <Grid container spacing={3} sx={{ px: isMobile ? 0 : 2 }}>
        {filteredData.length > 0 ? (
          filteredData.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    image={item.pic}
                    alt={`Prescription for ${item.DoctName}`}
                    onClick={() => handlePreviewImage(item.pic)}
                    sx={{
                      height: 250,
                      cursor: "zoom-in",
                      objectFit: "cover",
                      borderTopLeftRadius: 3,
                      borderTopRightRadius: 3,
                    }}
                  />
                  <Chip
                    label={item.STATUS}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      background:
                        item.STATUS === "Approved"
                          ? theme.palette.success.light
                          : theme.palette.warning.light,
                      fontWeight: 700,
                    }}
                  />
                </Box>

                <CardContent sx={{ bgcolor: theme.palette.background.paper }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {item.NAME}
                  </Typography>

                  <Grid container spacing={1}>
                    {[
                      { label: "Emp No", value: item.EmpNo },
                      { label: "Doctor", value: item.DoctName },
                      { label: "Category", value: item.DocCategory },
                      { label: "MCL Code", value: item.MclCode },
                      { label: "No of Strips", value: item.No_of_strips },
                      { label: "Chemist", value: item.Chemist_name },
                      { label: "DL_no", value: item.DL_no },
                      {
                        label: "Submitted",
                        value: new Date(item.SUBDATE).toLocaleDateString(),
                      },
                      { label: "Headquarters", value: item.HeadQrt },
                    ].map((field, i) => (
                      <Grid item xs={6} key={i}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 500,
                            }}
                          >
                            {field.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              wordBreak: "break-word",
                            }}
                          >
                            {field.value || "-"}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              py: 10,
            }}
          >
            <Typography variant="h6" color="textSecondary">
              No prescriptions found
            </Typography>
          </Box>
        )}
      </Grid>

      {/* Preview Modal */}
      <Modal open={!!previewImage} onClose={() => setPreviewImage(null)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.9)",
            backdropFilter: "blur(8px)",
          }}
        >
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: "absolute",
              top: 24,
              right: 24,
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={() => handleDownload(previewImage)}
            sx={{
              position: "absolute",
              top: 24,
              left: 24,
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <DownloadIcon />
          </IconButton>

          <Box
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              position: "relative",
              overflow: "hidden",
              borderRadius: 2,
              boxShadow: 6,
            }}
          >
            <img
              src={previewImage}
              alt="Prescription preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default PobImagePreviewModal;
